import { BN, web3 } from "@project-serum/anchor";
import { getGovernance } from "@solana/spl-governance";
import { AnchorWallet, WalletContextState } from "@solana/wallet-adapter-react";
import {
  AccountMeta,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import {
  CHAIN_ID,
  TREASURY_INDEX,
} from "../../../common/constants/common.constants";
import { ClubType } from "../../../common/enums/club.enum";
import { ClubAction } from "../../../common/enums/common.enums";
import { ProposalTypeForProgram } from "../../../common/enums/proposals.enums";
import {
  ITradingApprovalGovernanceData,
  IUpdateRoleWeight,
  IUpdateRoleWeightSolana,
} from "../../../common/interfaces/club-config.interface";
import { IClubTreasury } from "../../../common/interfaces/clubs.interface";
import { IInstructionSet } from "../../../common/interfaces/common.interface";
import { IProposalWithMetaData } from "../../../common/interfaces/proposals.interface";
import { clubsStore } from "../../../contexts/clubStore";
import { getTreasuryCount } from "../../../utilities/helpers";
import { updateVoterWeight } from "../../methods/proposals";
import { sendTransaction } from "../../transactions/sendTransaction";
import { sendTransactions } from "../../transactions/sendTransactions";
import {
  accountGovernanceSeed,
  changeClubConfigGovernance,
  ESCRROW_PROGRAM,
  governanceSeed,
  programFactory,
  proposalMetadataSeed,
  realmConfigSeed,
  RPC_CONNECTION,
  sellPermissionGovernanceSeed,
  splGovernanceProgram,
  transferSeed,
  treasuryDataSeed,
  treasurySeed,
  unqClubMemberSeed,
  unqClubSeed,
  vaultSeed,
  voterWeightSeed,
} from "../utilities";
import { getRemainingAccountForMemberOfNFTClub } from "./nftClubs.helpers";

export async function createUpdateApprovalQuorum(
  realmName: string,
  wallet: WalletContextState,
  payer: AnchorWallet,
  clubType: ClubType,
  clubTreasury: IClubTreasury,
  name: string,
  description: string,
  discussionLink: string,
  fundraiseCount: number,
  communityTokenMint: PublicKey,
  newVotingTime: number | null,
  governanceAccounts?: ITradingApprovalGovernanceData[],
  newApprovalQuorum?: number,
  governanceAccount?: PublicKey,
  nftMetadata?: PublicKey,
  membersNftTokenAccount?: PublicKey
) {
  const clubProgram = programFactory();
  const remainingAccounts: AccountMeta[] = [];
  const allInstructions: IInstructionSet[] = [];
  const instructions: TransactionInstruction[][] = [];
  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [clubData, cdBump] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [treasuryAddress, trBump] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );

  let [governanceAddress] = await PublicKey.findProgramAddress(
    [
      accountGovernanceSeed,
      realmAddress.toBuffer(),
      treasuryAddress.toBuffer(),
    ],
    splGovernanceProgram
  );

  if (clubTreasury.changeApprovalConfigGovernance) {
    governanceAddress = clubTreasury.changeApprovalConfigGovernance;
  }

  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmAddress.toBuffer(),
      communityTokenMint.toBuffer(),
      payer.publicKey!.toBuffer(),
    ],
    splGovernanceProgram
  );

  const accountGovernance = await getGovernance(
    RPC_CONNECTION,
    governanceAddress
  );
  let proposalIndexBuffer = Buffer.alloc(4);
  proposalIndexBuffer.writeInt32LE(accountGovernance.account.proposalCount, 0);

  let [proposalAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      accountGovernance.pubkey.toBuffer(),
      communityTokenMint.toBuffer(),
      proposalIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey!.toBuffer(),
    ],
    clubProgram.programId
  );

  const [proposalMetadata] = await PublicKey.findProgramAddress(
    [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
    clubProgram.programId
  );

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, clubTreasury.treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const [realmConfigPda] = await PublicKey.findProgramAddress(
    [realmConfigSeed, realmAddress.toBuffer()],
    splGovernanceProgram
  );

  try {
    const createProposalMetadataInstruction =
      clubProgram.instruction.createProposalMetadata(
        ProposalTypeForProgram.UpdateGovernanceConfig,
        null,
        null,
        null,
        {
          accounts: {
            clubData,
            realm: realmAddress,
            proposal: proposalAddress,
            proposalMetadata,
            memberData: memberPDA,
            governance: governanceAddress,
            treasuryData,
            payer: payer.publicKey!,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );

    instructions.push([createProposalMetadataInstruction]);

    allInstructions.push({
      description: "Creating proposal metadata",
      instructions: [createProposalMetadataInstruction],
    });

    const updateProposalDescriptionIx =
      clubProgram.instruction.updateProposalDescription(
        description,
        name,
        ["option YES"],
        discussionLink,
        {
          accounts: {
            proposal: proposalAddress,
            proposalMetadata,
            payer: payer.publicKey!,
          },
        }
      );

    allInstructions.push({
      description: "Updating proposal description",
      instructions: [updateProposalDescriptionIx],
    });

    instructions.push([updateProposalDescriptionIx]);

    let quorumBuffers: Buffer;

    let updateGovConfigRemaininAccounts: AccountMeta[] = [];
    let seeds: Buffer[][] = [];
    if (governanceAccounts) {
      const activeGov = governanceAccounts.find((g) => g.isChecked);
      if (!activeGov) {
        throw new Error("Can not find governance to update");
      }
      const { seeds: governedSeeds, governedAccounts } =
        await getGovernedAccountSeeds(
          clubTreasury,
          governanceAccount!,
          clubData,
          realmAddress,
          [activeGov]
        );
      seeds = governedSeeds;

      governedAccounts.forEach((g, index) => {
        updateGovConfigRemaininAccounts.push({
          isSigner: false,
          isWritable: true,
          pubkey: activeGov.publickey,
        });
        updateGovConfigRemaininAccounts.push({
          isSigner: false,
          isWritable: true,
          pubkey: g,
        });
      });

      quorumBuffers = Buffer.from([Number(activeGov.newQuorum)]);
    } else {
      quorumBuffers = Buffer.from([newApprovalQuorum!]);
      updateGovConfigRemaininAccounts.push({
        isSigner: false,
        isWritable: true,
        pubkey: governanceAccount!,
      });
      const { seeds: governedSeeds, governedAccounts } =
        await getGovernedAccountSeeds(
          clubTreasury,
          governanceAccount!,
          clubData,
          realmAddress
        );
      seeds = governedSeeds;
      updateGovConfigRemaininAccounts.push({
        isSigner: false,
        isWritable: true,
        pubkey: governedAccounts[0],
      });
    }

    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);
    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposalAddress.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );
    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmAddress,
      payer!,
      clubTreasury.treasury,
      fundraiseCount,
      ClubAction.UpdateGovernanceConfig,
      clubType,
      proposalAddress
    );
    const { clubBasicInfo } = clubsStore.getState();
    const gov = clubBasicInfo.governances.find(
      (g) => g.pubkey.toString() === governanceAddress.toString()
    )?.account.config.maxVotingTime;
    const newVt = clubBasicInfo.governances.find(
      (g) => g.pubkey.toString() === governanceAccount?.toString()
    )?.account.config.maxVotingTime;
    const createUpdateGovernanceConfig =
      clubProgram.instruction.updateGovernanceConfig(
        quorumBuffers,
        seeds,
        newVotingTime ? 86400 * newVotingTime + 1 : newVt,
        {
          accounts: {
            clubData,
            realm: realmAddress,
            memberData: memberPDA,
            proposal: proposalAddress,
            proposalMetadata: proposalMetadata,
            proposalTransaction: instructionAddress,
            treasury: clubTreasury.treasury,
            treasuryData: treasuryData,
            governance: clubTreasury.changeApprovalConfigGovernance!,
            tokenOwnerRecord: tokenOwnerRecord,
            voterWeightRecord: voterWeightAddress,
            communityTokenMint: communityTokenMint,
            payer: payer.publicKey!,
            realmConfig: realmConfigPda,
            rent: SYSVAR_RENT_PUBKEY,
            splGovernance: splGovernanceProgram,
            systemProgram: SystemProgram.programId,
          },
          remainingAccounts: updateGovConfigRemaininAccounts,
        }
      );

    instructions.push([updateVW, createUpdateGovernanceConfig]);

    allInstructions.push({
      description: "Creating proposal and updating voter weight",
      instructions: [updateVW, createUpdateGovernanceConfig],
    });

    const addressLookupTableAddresses: PublicKey[] = [
      realmAddress,
      governanceAddress,
      payer.publicKey,
      clubData,
      memberPDA,
      clubTreasury.treasury,
      treasuryData,
      realmConfigPda,
      proposalAddress,
      tokenOwnerRecord,
      proposalMetadata,
      instructionAddress,
      communityTokenMint,
      voterWeightAddress,
      splGovernanceProgram,
      SystemProgram.programId,
      SYSVAR_RENT_PUBKEY,
    ];

    remainingAccounts.forEach((remAcc) =>
      addressLookupTableAddresses.push(remAcc.pubkey)
    );

    // await sendVersionedTransaction({
    //   connection: RPC_CONNECTION,
    //   accountKeys: addressLookupTableAddresses,
    //   instructions: instructions,
    //   authority: wallet.publicKey!,
    //   payer: wallet,
    // });

    await sendTransactions(RPC_CONNECTION, payer, allInstructions);

    return proposalAddress;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const executeUpdateGovConfig = async (
  realmName: string,
  proposal: IProposalWithMetaData,
  treasury: PublicKey,
  wallet: AnchorWallet,
  governanceAccounts: PublicKey[],
  governedAccounts: PublicKey[]
) => {
  try {
    const clubProgram = programFactory();

    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData, cdBump] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [treasuryData] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
      clubProgram.programId
    );
    governanceAccounts.forEach((g) => console.log(g.toString(), "GOV"));

    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);
    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );

    const remainingAccounts: AccountMeta[] = [];
    governanceAccounts.forEach((acc, index) => {
      remainingAccounts.push({
        isSigner: false,
        isWritable: true,
        pubkey: acc,
      });
      remainingAccounts.push({
        isSigner: false,
        isWritable: true,
        pubkey: governedAccounts[index],
      });
    });
    const executeUpdateConfigIx =
      clubProgram.instruction.executeUpdateGovernanceConfig({
        accounts: {
          governance:
            proposal.proposalBasicData.proposalAccount.account.governance,
          proposal: proposal.proposalBasicData.proposalAccount.pubkey,
          proposalMetadata:
            proposal.proposalBasicData.proposalMetadata.proposalMetadata,
          realm: realmAddress,
          clubData,
          treasury: treasury,
          treasuryData,
          splGovernanceProgram: splGovernanceProgram,
          proposalTransaction: instructionAddress,
        },
        remainingAccounts,
      });

    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash: (await RPC_CONNECTION.getLatestBlockhash()).blockhash,
    });
    tx.add(executeUpdateConfigIx);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      transaction: signedTx,
      connection: RPC_CONNECTION,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const createUpdateRoleWeightProposal = async (
  realmName: string,
  payer: AnchorWallet,
  clubType: ClubType,
  clubTreasury: IClubTreasury,
  name: string,
  description: string,
  discussionLink: string,
  updateRoleConfig: IUpdateRoleWeight[],
  communityMint: PublicKey,
  fundraiseCount: number,
  nftMetadata?: PublicKey,
  membersNftTokenAccount?: PublicKey
) => {
  const clubProgram = programFactory();
  const remainingAccounts: AccountMeta[] = [];
  const allInstructions: IInstructionSet[] = [];
  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const mappedRoleConfig: IUpdateRoleWeightSolana[] = [];

  updateRoleConfig.forEach((roleConfig) => {
    if (roleConfig.role) {
      mappedRoleConfig.push({
        role: roleConfig.role,
        currentWeight: new BN(roleConfig.currentWeight),
        updateWeight: new BN(roleConfig.updateWeight * LAMPORTS_PER_SOL),
      });
    }
  });

  const [treasuryAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );

  let [governanceAddress] = await PublicKey.findProgramAddress(
    [
      accountGovernanceSeed,
      realmAddress.toBuffer(),
      treasuryAddress.toBuffer(),
    ],
    splGovernanceProgram
  );

  if (clubTreasury.changeApprovalConfigGovernance) {
    governanceAddress = clubTreasury.changeApprovalConfigGovernance;
  }

  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmAddress.toBuffer(),
      communityMint.toBuffer(),
      payer.publicKey.toBuffer(),
    ],
    splGovernanceProgram
  );

  const accountGovernance = await getGovernance(
    RPC_CONNECTION,
    governanceAddress
  );
  let proposalIndexBuffer = Buffer.alloc(4);
  proposalIndexBuffer.writeInt32LE(accountGovernance.account.proposalCount, 0);

  let [proposalAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      accountGovernance.pubkey.toBuffer(),
      communityMint.toBuffer(),
      proposalIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [proposalMetadata] = await PublicKey.findProgramAddress(
    [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
    clubProgram.programId
  );

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, clubTreasury.treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const optionIndex = 0;
  const transactionIndex = 0;
  const optionIndexBuffer = Buffer.alloc(1);
  optionIndexBuffer.writeUInt8(optionIndex, 0);
  const instructionIndexBuffer = Buffer.alloc(2);
  instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

  const [instructionAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      proposalAddress.toBuffer(),
      optionIndexBuffer,
      instructionIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [realmConfigPda] = await PublicKey.findProgramAddress(
    [realmConfigSeed, realmAddress.toBuffer()],
    splGovernanceProgram
  );

  try {
    const createProposalMetadataInstruction =
      clubProgram.instruction.createProposalMetadata(
        ProposalTypeForProgram.UpdateRoleConfig,
        null,
        null,
        null,
        {
          accounts: {
            clubData,
            realm: realmAddress,
            proposal: proposalAddress,
            proposalMetadata,
            memberData: memberPDA,
            governance: governanceAddress,
            treasuryData,
            payer: payer.publicKey,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );

    allInstructions.push({
      description: "Creating proposal metadata",
      instructions: [createProposalMetadataInstruction],
    });

    const updateProposalDescriptionIx =
      clubProgram.instruction.updateProposalDescription(
        description,
        name,
        ["option YES"],
        discussionLink,
        {
          accounts: {
            proposal: proposalAddress,
            proposalMetadata,
            payer: payer.publicKey,
          },
        }
      );

    allInstructions.push({
      description: "Updating proposal description",
      instructions: [updateProposalDescriptionIx],
    });

    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmAddress,
      payer,
      clubTreasury.treasury,
      fundraiseCount,
      ClubAction.UpdateGovernanceConfig,
      clubType,
      proposalAddress
    );

    const createUpdateRoleWeightIx =
      clubProgram.instruction.createUpdateRoleProposal(mappedRoleConfig, {
        accounts: {
          realm: realmAddress,
          clubData,
          proposal: proposalAddress,
          proposalTransaction: instructionAddress,
          treasury: clubTreasury.treasury,
          treasuryData: treasuryData,
          memberData: memberPDA,
          communityMint,
          tokenOwnerRecord,
          payer: payer.publicKey,
          voterWeightRecord: voterWeightAddress,
          governance: governanceAddress,
          realmConfig: realmConfigPda,
          proposalMetadata,
          escrowProgram: ESCRROW_PROGRAM,
          splGovernanceProgram,
          systemProgram: SystemProgram.programId,
          rent: SYSVAR_RENT_PUBKEY,
        },
      });
    allInstructions.push({
      description: "Update voter weight, create proposal",
      instructions: [updateVW, createUpdateRoleWeightIx],
    });
    await sendTransactions(RPC_CONNECTION, payer, allInstructions);
    return proposalAddress;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const executeUpdateRoleWeight = async (
  realmName: string,
  proposal: IProposalWithMetaData,
  treasury: PublicKey,
  payer: AnchorWallet
) => {
  try {
    const clubProgram = programFactory();
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [proposalMetadata] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        proposalMetadataSeed,
      ],
      clubProgram.programId
    );

    const [treasuryData] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
      clubProgram.programId
    );
    const executeUpdateRoleIx = clubProgram.instruction.executeUpdateRole({
      accounts: {
        clubData,
        governance:
          proposal.proposalBasicData.proposalAccount.account.governance,
        proposal: proposal.proposalBasicData.proposalAccount.pubkey,
        proposalMetadata,
        proposalTransaction: proposal.proposalTransactionInfo?.pubkey!,
        realm: realmAddress,
        treasuryData,
        escrowProgram: ESCRROW_PROGRAM,
        splGovernanceProgram: splGovernanceProgram,
      },
    });
    const tx = new Transaction({
      feePayer: payer.publicKey,
      recentBlockhash: (await RPC_CONNECTION.getLatestBlockhash()).blockhash,
    });
    tx.add(executeUpdateRoleIx);
    const signedTx = await payer.signTransaction(tx);
    await sendTransaction({
      transaction: signedTx,
      connection: RPC_CONNECTION,
      wallet: payer,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getGovernedAccountSeeds = async (
  treasury: IClubTreasury,
  governanceAccount: PublicKey,
  clubData: PublicKey,
  realmAddress: PublicKey,
  sellPermissionGovernances?: ITradingApprovalGovernanceData[]
): Promise<{ seeds: Buffer[][]; governedAccounts: PublicKey[] }> => {
  try {
    const seeds: Buffer[][] = [];
    const bumpBuff = Buffer.alloc(1);
    const clubProgram = programFactory();
    let governedAccounts: PublicKey[] = [];
    switch (governanceAccount.toString()) {
      case treasury.governance.toString(): {
        const [treasuryAddress, treasuryBump] =
          await PublicKey.findProgramAddress(
            [
              unqClubSeed,
              clubData.toBuffer(),
              treasurySeed,
              getTreasuryCount(TREASURY_INDEX),
            ],
            clubProgram.programId
          );
        bumpBuff.writeUInt8(treasuryBump, 0);
        seeds.push([
          unqClubSeed,
          clubData.toBuffer(),
          treasurySeed,
          getTreasuryCount(TREASURY_INDEX),
          bumpBuff,
        ]);
        governedAccounts.push(treasuryAddress);
        break;
      }
      case treasury.withdrawalGovernance?.toString(): {
        const [vaultAddress, vaultBump] = await PublicKey.findProgramAddress(
          [
            unqClubSeed,
            clubData.toBuffer(),
            treasury.treasury.toBuffer(),
            vaultSeed,
            Buffer.from(CHAIN_ID),
          ],
          clubProgram.programId
        );
        bumpBuff.writeUInt8(vaultBump, 0);
        seeds.push([
          unqClubSeed,
          clubData.toBuffer(),
          treasury.treasury.toBuffer(),
          vaultSeed,
          Buffer.from(CHAIN_ID),
          bumpBuff,
        ]);
        governedAccounts.push(vaultAddress);

        break;
      }
      case treasury.transferGovernance?.toString(): {
        const [vaultAddress, vaultBump] = await PublicKey.findProgramAddress(
          [unqClubSeed, treasury.treasury.toBuffer(), transferSeed],
          clubProgram.programId
        );
        bumpBuff.writeUInt8(vaultBump, 0);
        seeds.push([
          unqClubSeed,
          treasury.treasury.toBuffer(),
          transferSeed,
          bumpBuff,
        ]);
        governedAccounts.push(vaultAddress);

        break;
      }
      case treasury.changeApprovalConfigGovernance?.toString(): {
        const [governedAccoun, bump] = PublicKey.findProgramAddressSync(
          [
            unqClubSeed,
            treasury.treasury.toBuffer(),
            changeClubConfigGovernance,
          ],
          clubProgram.programId
        );

        bumpBuff.writeUInt8(bump, 0);
        seeds.push([
          unqClubSeed,
          treasury.treasury.toBuffer(),
          changeClubConfigGovernance,
          bumpBuff,
        ]);
        governedAccounts.push(governedAccoun);
        break;
      }

      default: {
        for (const governedAcc of sellPermissionGovernances!) {
          const spcBuffer = Buffer.alloc(4);
          spcBuffer.writeInt32LE(
            treasury.sellPermissions.findIndex(
              (sp) =>
                sp.governance?.toString() === governedAcc.publickey.toString()
            ),
            0
          );
          const [spcGovernedAccount, spcBump] =
            await PublicKey.findProgramAddress(
              [
                unqClubSeed,
                realmAddress.toBuffer(),
                sellPermissionGovernanceSeed,
                spcBuffer,
              ],
              clubProgram.programId
            );
          bumpBuff.writeUInt8(spcBump, 0);
          seeds.push([
            unqClubSeed,
            realmAddress.toBuffer(),
            sellPermissionGovernanceSeed,
            spcBuffer,
            bumpBuff,
          ]);
          governedAccounts.push(spcGovernedAccount);
        }
      }
    }
    return { seeds, governedAccounts };
  } catch (error) {
    throw error;
  }
};

export const createSellPermissionProposal = async (
  sellPermission: ITradingApprovalGovernanceData,
  clubTreasury: IClubTreasury,
  name: string,
  description: string,
  discussionLink: string,
  realmName: string,
  clubType: ClubType,
  communityTokenMint: PublicKey,
  payer: AnchorWallet,
  nftMetadata?: PublicKey,
  membersNftTokenAccount?: PublicKey
) => {
  const clubProgram = programFactory();
  const remainingAccounts: AccountMeta[] = [];
  const allInstructions: IInstructionSet[] = [];
  const instructions: TransactionInstruction[][] = [];
  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [clubData, cdBump] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [treasuryAddress, trBump] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );

  let [governanceAddress] = await PublicKey.findProgramAddress(
    [
      accountGovernanceSeed,
      realmAddress.toBuffer(),
      treasuryAddress.toBuffer(),
    ],
    splGovernanceProgram
  );

  if (clubTreasury.changeApprovalConfigGovernance) {
    governanceAddress = clubTreasury.changeApprovalConfigGovernance;
  }

  const accountGovernance = await getGovernance(
    RPC_CONNECTION,
    governanceAddress
  );
  let proposalIndexBuffer = Buffer.alloc(4);
  proposalIndexBuffer.writeInt32LE(accountGovernance.account.proposalCount, 0);

  let [proposalAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      accountGovernance.pubkey.toBuffer(),
      communityTokenMint.toBuffer(),
      proposalIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey!.toBuffer(),
    ],
    clubProgram.programId
  );

  const [proposalMetadata] = await PublicKey.findProgramAddress(
    [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
    clubProgram.programId
  );

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, clubTreasury.treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const [realmConfigPda] = await PublicKey.findProgramAddress(
    [realmConfigSeed, realmAddress.toBuffer()],
    splGovernanceProgram
  );

  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmAddress.toBuffer(),
      communityTokenMint.toBuffer(),
      payer.publicKey!.toBuffer(),
    ],
    splGovernanceProgram
  );

  const optionIndex = 0;
  const transactionIndex = 0;
  const optionIndexBuffer = Buffer.alloc(1);
  optionIndexBuffer.writeUInt8(optionIndex, 0);
  const instructionIndexBuffer = Buffer.alloc(2);
  instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

  const [instructionAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      proposalAddress.toBuffer(),
      optionIndexBuffer,
      instructionIndexBuffer,
    ],
    splGovernanceProgram
  );

  try {
    const createProposalMetadataInstruction =
      clubProgram.instruction.createProposalMetadata(
        ProposalTypeForProgram.AddSellPermission,
        null,
        null,
        null,
        {
          accounts: {
            clubData,
            realm: realmAddress,
            proposal: proposalAddress,
            proposalMetadata,
            memberData: memberPDA,
            governance: governanceAddress,
            treasuryData,
            payer: payer.publicKey!,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );

    instructions.push([createProposalMetadataInstruction]);

    allInstructions.push({
      description: "Creating proposal metadata",
      instructions: [createProposalMetadataInstruction],
    });

    const updateProposalDescriptionIx =
      clubProgram.instruction.updateProposalDescription(
        description,
        name,
        ["option YES"],
        discussionLink,
        {
          accounts: {
            proposal: proposalAddress,
            proposalMetadata,
            payer: payer.publicKey!,
          },
        }
      );

    allInstructions.push({
      description: "Updating proposal description",
      instructions: [updateProposalDescriptionIx],
    });

    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmAddress,
      payer!,
      clubTreasury.treasury,
      clubTreasury.fundraiseCount,
      ClubAction.UpdateGovernanceConfig,
      clubType,
      proposalAddress
    );

    instructions.push([updateProposalDescriptionIx]);
    const ix = await clubProgram.methods
      .createAddSellPermissionProposal(
        true,
        {
          decimal: clubTreasury.denominatedTokenInfo
            ? clubTreasury.denominatedTokenInfo.decimals
            : 9,
          from: new BN(sellPermission.from),
          quorumMinimum: +sellPermission.newQuorum,
          to: new BN(sellPermission.to),
        },
        86400 * 30 + 1
      )
      .accounts({
        clubData,
        treasuryData: clubTreasury.treasuryDataAddress,
        governanceAuthority: payer.publicKey,
        payer: payer.publicKey,
        proposal: proposalAddress,
        realm: realmAddress,
        proposalMetadata: proposalMetadata,
        tokenOwnerRecord,
        realmConfig: realmConfigPda,
        governance: clubTreasury.changeApprovalConfigGovernance,
        communityTokenMint,
        voterWeightRecord: voterWeightAddress,
        proposalTransactionAddress: instructionAddress,
        rent: SYSVAR_RENT_PUBKEY,
        splGovernanceProgram,
        systemProgram: SystemProgram.programId,
      })
      .instruction();

    allInstructions.push({
      description: "Create sell permission proposal",
      instructions: [updateVW, ix],
    });

    await sendTransactions(RPC_CONNECTION, payer, allInstructions);
  } catch (error) {
    throw error;
  }
};

export const executeAddSellPermissionProposal = async (
  treasury: IClubTreasury,
  realmName: string,
  payer: AnchorWallet,
  proposal: IProposalWithMetaData,
  communityMint: PublicKey
) => {
  const clubProgram = programFactory();
  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [clubData, cdBump] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [realmConfigPda] = await PublicKey.findProgramAddress(
    [realmConfigSeed, realmAddress.toBuffer()],
    splGovernanceProgram
  );

  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmAddress.toBuffer(),
      communityMint.toBuffer(),
      payer.publicKey!.toBuffer(),
    ],
    splGovernanceProgram
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey!.toBuffer(),
    ],
    clubProgram.programId
  );

  const [voterWeightAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      voterWeightSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const updateVW = clubProgram.instruction.updateVoterWeightForGovernance({
    accounts: {
      realm: realmAddress,
      clubData,
      voterWeightRecord: voterWeightAddress,
      memberData: memberPDA,
      payer: payer.publicKey,
      systemProgram: web3.SystemProgram.programId,
    },
  });

  const spcCountBuffer = Buffer.alloc(4);
  spcCountBuffer.writeUInt32LE(
    treasury.sellPermissions.findIndex(
      (sp) => sp.governance?.toString() === SystemProgram.programId.toString()
    ),
    0
  );

  const [governedAcc] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      realmAddress.toBuffer(),
      sellPermissionGovernanceSeed,
      spcCountBuffer,
    ],
    clubProgram.programId
  );

  const [governanceAddress] = await PublicKey.findProgramAddress(
    [accountGovernanceSeed, realmAddress.toBuffer(), governedAcc.toBuffer()],
    splGovernanceProgram
  );

  try {
    const executeIx = await clubProgram.methods
      .executeSellPermissionTransaction()
      .accounts({
        clubProgram: clubProgram.programId,
        governance: treasury.changeApprovalConfigGovernance,
        clubData,
        realm: realmAddress,
        memberData: memberPDA,
        payer: payer.publicKey,
        proposal: proposal.proposalBasicData.proposalAccount.pubkey,
        proposalMetadata:
          proposal.proposalBasicData.proposalMetadata.proposalMetadata,
        proposalTransaction: proposal.proposalTransactionInfo?.pubkey,
        realmConfig: realmConfigPda,
        treasuryData: treasury.treasuryDataAddress,
        tokenOwnerRecord: tokenOwnerRecord,
        voterWeightRecord: voterWeightAddress,
        splGovernanceProgram,
        systemProgram: SystemProgram.programId,
        spcGovernance: governanceAddress,
        spcGovernedAccount: governedAcc,
      })
      .instruction();

    const allInstructions: IInstructionSet[] = [
      {
        description: "Update voter weight, execute proposal",
        instructions: [updateVW, executeIx],
      },
    ];

    await sendTransactions(RPC_CONNECTION, payer, allInstructions);
  } catch (error) {
    console.log(error);
    throw error;
  }
};
